import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Send, X } from "lucide-react";
import toast from "react-hot-toast";
import { api, type ApiResponse } from "@/api/client";
import { studioKeys, type QueueData } from "./studioTypes";

/**
 * NL Studio "Queue" tab (block 4.15 řez 2) — scheduled campaigns (reschedule / send now /
 * cancel) + in-flight campaigns with wave progress.
 */

function nextMondayGeneration(): string {
  const now = new Date();
  const d = new Date(now);
  const day = d.getUTCDay(); // 0 Sun … 6 Sat
  let add = (1 - day + 7) % 7;
  if (add === 0 && now.getUTCHours() >= 6) add = 7;
  d.setUTCDate(d.getUTCDate() + add);
  d.setUTCHours(6, 0, 0, 0);
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relativeTime(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.round((abs % 3_600_000) / 60_000);
  const span = h > 0 ? `${h} h ${m} min` : `${m} min`;
  return diffMs >= 0 ? `in ${span}` : `${span} ago`;
}

export default function QueuePanel() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: studioKeys.queue,
    queryFn: () => api.get<ApiResponse<QueueData>>("/api/admin/newsletter-studio/queue"),
    refetchInterval: (q) => ((q.state.data?.data?.sending?.length ?? 0) > 0 ? 5000 : 30000),
  });
  const queue = data?.data;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: studioKeys.queue });
    queryClient.invalidateQueries({ queryKey: studioKeys.drafts });
    // Unschedule moves campaigns back to drafts and send-now eventually lands them in
    // Results — the sent-campaigns table caches the same endpoint under its own key.
    queryClient.invalidateQueries({ queryKey: studioKeys.results });
  };

  if (isLoading || !queue) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600">
        <span>
          Next AI edition: <strong>{nextMondayGeneration()}</strong> (Mon 06:00 UTC)
        </span>
        <span>
          Send rate: <strong>{queue.ratePerMinute}/min</strong>
        </span>
        <span>
          Subject-test wave 2: <strong>+{queue.wave2DelayHours} h</strong> after wave 1
        </span>
      </div>

      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Scheduled ({queue.scheduled.length})
        </h3>
        {queue.scheduled.length === 0 ? (
          <div className="card py-8 text-center text-sm text-gray-500">
            Nothing scheduled — pick a draft and use “Schedule” in the send dialog.
          </div>
        ) : (
          <div className="space-y-3">
            {queue.scheduled.map((s) => (
              <ScheduledRow key={s.id} item={s} onChanged={refresh} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Sending now ({queue.sending.length})
        </h3>
        {queue.sending.length === 0 ? (
          <div className="card py-8 text-center text-sm text-gray-500">No campaign in flight.</div>
        ) : (
          <div className="space-y-3">
            {queue.sending.map((s) => (
              <SendingRow key={s.id} item={s} wave2DelayHours={queue.wave2DelayHours} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ScheduledRow({
  item,
  onChanged,
}: {
  item: QueueData["scheduled"][number];
  onChanged: () => void;
}) {
  const [newTime, setNewTime] = useState("");
  const [busy, setBusy] = useState<null | "send" | "cancel" | "move">(null);

  const act = async (kind: "send" | "cancel" | "move") => {
    setBusy(kind);
    try {
      if (kind === "send") {
        const audience = item.segmentName ? `segment “${item.segmentName}”` : "the full audience";
        if (!window.confirm(`Send “${item.subject}” to ${audience} NOW?`)) return;
        const res = await api.post<{ message?: string }>(
          `/api/admin/newsletter/campaigns/${item.id}/send`,
          { segmentId: item.segmentId ?? undefined },
        );
        // The route's message says what actually happens (single wave vs "Wave 1 of N …").
        toast.success(res.message ?? "Sending");
      } else if (kind === "cancel") {
        if (!window.confirm("Cancel this schedule? The campaign goes back to drafts.")) return;
        await api.post(`/api/admin/newsletter/campaigns/${item.id}/unschedule`, {});
        toast.success("Schedule cancelled");
      } else {
        await api.post(`/api/admin/newsletter/campaigns/${item.id}/reschedule`, {
          scheduledFor: new Date(newTime).toISOString(),
        });
        toast.success("Rescheduled");
        setNewTime("");
      }
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-gray-900">{item.subject}</p>
        <p className="text-xs text-gray-500">
          {item.scheduledAt
            ? `${new Date(item.scheduledAt).toLocaleString()} · ${relativeTime(item.scheduledAt)}`
            : "—"}
          {item.segmentName ? ` · segment: ${item.segmentName}` : ""}
          {item.isAi ? " · AI edition" : " · manual"}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="datetime-local"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
        />
        <button
          onClick={() => act("move")}
          disabled={!newTime || busy !== null}
          className="btn-secondary"
        >
          {busy === "move" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reschedule"}
        </button>
        <button onClick={() => act("send")} disabled={busy !== null} className="btn-secondary">
          {busy === "send" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Send now
        </button>
        <button
          onClick={() => act("cancel")}
          disabled={busy !== null}
          className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50"
          title="Cancel schedule"
        >
          {busy === "cancel" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

function SendingRow({
  item,
  wave2DelayHours,
}: {
  item: QueueData["sending"][number];
  wave2DelayHours: number;
}) {
  const { total, sent, failed, queued, wave2Queued } = item.counts;
  const done = sent + failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const ws = item.waveState;
  const waveInfo = (() => {
    if (!ws?.wave1At || wave2Queued === 0) return null;
    if (ws.winnerVariant === undefined) {
      const due = new Date(new Date(ws.wave1At).getTime() + wave2DelayHours * 3_600_000);
      return `Subject test: wave 1 out, winner by clicks ~${due.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → wave 2 (${wave2Queued} waiting)`;
    }
    return `Wave 2 rolling out with variant ${String.fromCharCode(65 + (ws.winnerVariant ?? 0))}`;
  })();

  return (
    <div className="card space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate font-medium text-gray-900">{item.subject}</p>
        <p className="whitespace-nowrap text-sm text-gray-600">
          {sent}/{total} sent
          {failed > 0 && <span className="ml-1 text-red-600">· {failed} failed</span>}
        </p>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full ${failed > 0 ? "bg-amber-500" : "bg-green-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-gray-500">
        {queued} queued{waveInfo ? ` · ${waveInfo}` : ""}
        {item.sentAt ? ` · started ${new Date(item.sentAt).toLocaleTimeString()}` : ""}
      </p>
    </div>
  );
}
